import { License, ResolverTag } from '@ps/web-core-enums';
import classNames from 'clsx';
import React from 'react';
import { useSelector } from 'react-redux';

import ResolverLink from '@swf/components/ResolverLink/ResolverLink';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';

import {
  adictel,
  adm,
  admStar,
  alwaysPlayLegally,
  anj,
  autoProhibicion,
  container,
  evalujeu,
  footer,
  fpsDe,
  fpsEn,
  fpsNl,
  gambleAware,
  gamblingCommission,
  gameCare,
  gameCareUk,
  gamingLabs,
  gamstop,
  gg,
  gordonMoody,
  grandCasino,
  hellenic,
  ibas,
  ibia,
  juegoSeguro,
  jugarBien,
  keoea,
  logo,
  logos,
  mga,
  plus18,
  plus21,
  rg,
  sal,
  sicad,
  sol,
  stopSpillet
} from './Footer.scss';

enum Locale {
  NL = 'nl',
  DE = 'de'
}

const getFpsLogo = (locale: string) => {
  const logo = {
    [Locale.NL]: fpsNl,
    [Locale.DE]: fpsDe
  };
  return logo[locale] || fpsEn;
};

const getLogos = (license: License, locale: string) => {
  const map: Partial<{ [key in License]: string[] }> = {
    [License.COM]: [gameCare, gordonMoody, plus18, mga, ibia, rg],
    [License.IT]: [admStar, adm, plus18],
    [License.FR]: [evalujeu, adictel, anj, plus18],
    [License.EE]: [plus21],
    [License.BE]: [getFpsLogo(locale), grandCasino, alwaysPlayLegally, plus21],
    [License.ES]: [juegoSeguro, jugarBien, autoProhibicion, plus18],
    [License.DK]: [stopSpillet, gg, plus18],
    [License.EU]: [gameCare, gordonMoody, plus18, mga, ibia, rg],
    [License.UK]: [gambleAware, gamstop, gamblingCommission, gameCareUk, ibas, plus18],
    [License.BG]: [plus18],
    [License.RU]: [mga, plus18],
    [License.GR]: [keoea, plus21, hellenic],
    [License.PT]: [sicad],
    [License.RO]: [sal, sol]
  };
  return map[license];
};

interface Props {
  showFooter?: boolean;
  modal?: boolean;
}

const Footer: React.FC<Props> = ({ showFooter, modal }) => {
  const license = useSelector(select.account.getLicense);
  const locale = useSelector(select.locale.get);
  const isEmbedded = useSelector(select.device.isEmbedded);
  const {
    data: { cmsHeaderAndGridLayout }
  } = useWizardContext();

  const getResolverTag = (logo: string) => {
    if (logo === plus18) {
      return ResolverTag.AboutRgCashierLogo;
    }
    const linksList = {
      [License.BE]: {
        [plus21]: ResolverTag.ResponsibleGamingLogoBe
      },
      [License.COM]: {
        [rg]: ResolverTag.HelpRGAccredation,
        [ibia]: ResolverTag.IbiaLogo,
        [gameCare]: ResolverTag.HelpGamecare,
        [gordonMoody]: ResolverTag.GordonMoodyLogo,
        [mga]: ResolverTag.HelpMGA,
        [gamingLabs]: ResolverTag.GamingLabsLogo
      },
      [License.EU]: {
        [rg]: ResolverTag.HelpRGAccredation,
        [gameCare]: ResolverTag.HelpGamecare,
        [gordonMoody]: ResolverTag.GordonMoodyLogo,
        [mga]: ResolverTag.HelpMGA,
        [ibia]: ResolverTag.IbiaLogo,
        [gamingLabs]: ResolverTag.GamingLabsLogo
      },
      [License.UK]: {
        [gambleAware]: ResolverTag.BeGambleAwareLogo,
        [gamblingCommission]: ResolverTag.UKLicenseAccountNumber,
        [gameCareUk]: ResolverTag.GamCareHelplineLogo,
        [gamstop]: ResolverTag.UKGamstopMain,
        [ibas]: ResolverTag.IbasLogo
      },
      [License.RO]: {
        [sal]: ResolverTag.SalLgo,
        [sol]: ResolverTag.SolLgo
      }
    };
    return linksList[license] && linksList[license][logo];
  };

  const wizardFooter = (
    <footer className={footer}>
      <div className={container}>
        <div className={logos}>
          <ResolverLink resolverTag={ResolverTag.GamingLabsLogo} key={gamingLabs}>
            <div className={classNames(logo, gamingLabs)} data-testid={gamingLabs} />
          </ResolverLink>
          {getLogos(license, locale)?.map((className, key) => {
            const resolverTag = getResolverTag(className);
            const item = (
              <div key={key} data-testid={className} className={classNames(logo, className)} />
            );
            return resolverTag ? (
              <ResolverLink resolverTag={resolverTag} key={key}>
                {item}
              </ResolverLink>
            ) : (
              item
            );
          })}
        </div>
      </div>
    </footer>
  );

  const getFooterType = () => {
    if (cmsHeaderAndGridLayout) {
      if (isEmbedded) {
        return wizardFooter;
      }
      return null;
    } else {
      if (showFooter) {
        return wizardFooter;
      }
      return null;
    }
  };

  return !modal ? getFooterType() : null;
};

export default Footer;
